import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useToast } from '../../context/ToastContext';
import { useLoading } from '../../context/LoadingContext';
import {
  getSystemSettings,
  createSystemInvoice,
  getCompanyByID
} from '../../services/api/SuperAdminApi';
import './SystemInvoiceForm.scss';

export const SystemInvoiceForm = () => {
  const { companyId } = useParams();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { showLoading, hideLoading } = useLoading();

  const [companyData, setCompanyData] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  const [formData, setFormData] = useState({
    invoiceNumber: '',
    issuedTo: companyId,
    subtotal: 0,
    cgstAmount: 0,
    sgstAmount: 0,
    igstAmount: 0,
    cgstPercentage: 0,
    sgstPercentage: 0,
    igstPercentage: 0,
    totalAmount: 0,
  });

  const [itemData, setItemData] = useState({
    name: 'LawSuit Pro (www.lawsuit-pro.com)',
    description: '',
    qty: 1,
    rate: 0,
    amount: 0,
  });

  const rateInputRef = useRef(null);
  const quantityInputRef = useRef(null);

  useEffect(() => {
    const fetchCompanyData = async () => {
      try {
        showLoading('Loading company details...');
        const response = await getCompanyByID(companyId);
        console.log('Company Data:', response.data);
        setCompanyData(response.data);
        
        // Set rate from planData.price
        const planPrice = response.data?.planData?.price || 0;
        const planName = response.data?.planData?.name || 'Subscription Plan';
        
        setItemData(prev => ({
          ...prev,
          rate: planPrice,
          amount: planPrice * prev.qty,
          description: `Subscription Plan: ${planName}`
        }));

        hideLoading();
      } catch (error) {
        console.error('Error fetching company data:', error);
        showToast('Failed to fetch company details', 'error');
        hideLoading();
      }
    };

    if (companyId) {
      fetchCompanyData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  // Calculate totals when item or company data changes
  useEffect(() => {
    if (companyData && itemData.amount > 0) {
      const subtotal = itemData.amount;
      const companyState = companyData?.company?.state?.trim().toLowerCase() || '';
      const isGujarat = companyState === 'gujarat';

      let cgstAmount = 0;
      let sgstAmount = 0;
      let igstAmount = 0;
      let cgstPercentage = 0;
      let sgstPercentage = 0;
      let igstPercentage = 0;

      if (isGujarat) {
        cgstPercentage = 9;
        sgstPercentage = 9;
        cgstAmount = (subtotal * cgstPercentage) / 100;
        sgstAmount = (subtotal * sgstPercentage) / 100;
        
      } else {
        igstPercentage = 18;
        igstAmount = (subtotal * igstPercentage) / 100;
      }

      const totalAmount = subtotal + cgstAmount + sgstAmount + igstAmount;

      setFormData(prev => ({
        ...prev,
        subtotal,
        cgstAmount,
        sgstAmount,
        igstAmount,
        cgstPercentage,
        sgstPercentage,
        igstPercentage,
        totalAmount
      }));
    }
  }, [itemData.amount, companyData]);

  const handleItemChange = (field, value) => {
    setItemData(prev => {
      // Convert numeric fields to numbers
      const processedValue = (field === 'qty' || field === 'rate') 
        ? parseFloat(value) || 0 
        : value;
      
      const updated = { ...prev, [field]: processedValue };
      
      if (field === 'qty' || field === 'rate') {
        const qty = field === 'qty' ? processedValue : prev.qty;
        const rate = field === 'rate' ? processedValue : prev.rate;
        updated.amount = qty * rate;
      }
      
      return updated;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!itemData.rate || itemData.rate <= 0) {
      showToast('Please enter a valid rate', 'error');
      return;
    }

    if (!itemData.qty || itemData.qty <= 0) {
      showToast('Please enter a valid quantity', 'error');
      return;
    }

    try {
      setIsSaving(true);
      showLoading('Creating invoice...');

      // Get invoice number
      const settingsResponse = await getSystemSettings();
      const invoiceSettings = settingsResponse.data;
      const invoiceNumber = `${invoiceSettings.invoiceNo.text}/${invoiceSettings.invoiceNo.count + 1}`;

      // Prepare payload
      const payload = {
        invoiceNumber,
        issuedTo: companyId,
        items: [{
          name: itemData.name,
          description: itemData.description,
          qty: itemData.qty,
          rate: Number(itemData.rate),
          amount: itemData.amount
        }],
        subtotal: formData.subtotal,
        igstAmount: formData.igstAmount,
        igstPercentage: formData.igstPercentage,
        cgstAmount: formData.cgstAmount,
        sgstAmount: formData.sgstAmount,
        cgstPercentage: formData.cgstPercentage,
        sgstPercentage: formData.sgstPercentage,
        totalAmount: formData.totalAmount
      };

      console.log('Creating system invoice with payload:', payload);

      // Create invoice (backend will handle invoice number increment)
      await createSystemInvoice(payload);

      showToast('System invoice created successfully!', 'success');
      hideLoading();
      
      // Navigate back to company details page
      navigate(`/home/superadmin/companydetails/${companyId}`);
    } catch (error) {
      console.error('Error creating system invoice:', error);
      showToast(
        error.response?.data?.message || 'Failed to create invoice. Please try again.',
        'error'
      );
      hideLoading();
    } finally {
      setIsSaving(false);
    }
  };

  const isFormValid = () => {
    return itemData.rate > 0 && itemData.qty > 0 && itemData.amount > 0;
  };

  if (!companyData) {
    return <div className="system-invoice-form-container">Loading...</div>;
  }

  return (
    <div className="system-invoice-form-container">
      <div className="system-invoice-form-wrapper">
        <div className="system-invoice-form-header">
          <h1 className="system-invoice-form-title">Create System Invoice</h1>
          <p className="company-name">For: {companyData?.company?.companyName}</p>
        </div>

        <hr className="system-invoice-form-divider" />

        <form className="system-invoice-form-content" onSubmit={handleSubmit}>
          {/* Item Details Section */}
          <div className="form-section">
            <h3>Service Details</h3>
            
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="itemName">Service Name:</label>
                <input
                  type="text"
                  id="itemName"
                  value={itemData.name}
                  disabled
                  className="readonly-input"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="description">Description:</label>
                <input
                  type="text"
                  id="description"
                  value={itemData.description}
                  disabled
                  className="readonly-input"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="quantity">
                  Quantity <span className="system-invoice-form-required">*</span>
                </label>
                <input
                  ref={quantityInputRef}
                  type="number"
                  id="quantity"
                  min="1"
                  value={itemData.qty}
                  onChange={(e) => handleItemChange('qty', e.target.value)}
                  onWheel={(e) => e.target.blur()}
                  className="no-spinner"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="rate">
                  Rate (₹) <span className="system-invoice-form-required">*</span>
                </label>
                <input
                  ref={rateInputRef}
                  type="number"
                  id="rate"
                  min="0"
                //   step="0.01"
                  value={itemData.rate}
                  onChange={(e) => handleItemChange('rate', e.target.value)}
                  onWheel={(e) => e.target.blur()}
                  className="no-spinner"
                  required
                />
              </div>

              <div className="form-group">
                <label>Amount (₹)</label>
                <div className="amount-display">
                  ₹{itemData.amount.toFixed(2)}
                </div>
              </div>
            </div>
          </div>

          {/* Invoice Summary Section */}
          <div className="form-section">
            <h3>Invoice Summary</h3>
            <div className="summary">
              <p>
                <strong>Subtotal:</strong> ₹{formData.subtotal.toFixed(2)}
              </p>
              {formData.cgstPercentage > 0 && (
                <>
                  <p>
                    <strong>CGST ({formData.cgstPercentage}%):</strong> ₹{formData.cgstAmount.toFixed(2)}
                  </p>
                  <p>
                    <strong>SGST ({formData.sgstPercentage}%):</strong> ₹{formData.sgstAmount.toFixed(2)}
                  </p>
                </>
              )}
              {formData.igstPercentage > 0 && (
                <p>
                  <strong>IGST ({formData.igstPercentage}%):</strong> ₹{formData.igstAmount.toFixed(2)}
                </p>
              )}
              <p className="total-amount">
                <strong>Total Amount:</strong> ₹{formData.totalAmount.toFixed(2)}
              </p>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="form-actions">
            <button
              type="button"
              className="btn-secondary"
              onClick={() => navigate(`/home/superadmin/companydetails/${companyId}`)}
              disabled={isSaving}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={isSaving || !isFormValid()}
            >
              {isSaving ? 'Creating Invoice...' : 'Create Invoice'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
